import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface SEOHeadProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonical?: string;
  jsonLd?: object | object[];
  ogImage?: string;
}

export default function SEOHead({
  title,
  description,
  keywords,
  canonical,
  jsonLd,
  ogImage,
}: SEOHeadProps) {
  const location = useLocation();
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://openclaw.cc';

  useEffect(() => {
    // Update title
    if (title) {
      document.title = title;
    }

    // Update meta tags
    const metaTags: Array<{ name?: string; property?: string; content: string }> = [];

    if (description) {
      metaTags.push(
        { name: 'description', content: description },
        { property: 'og:description', content: description },
        { name: 'twitter:description', content: description }
      );
    }

    if (keywords) {
      metaTags.push({ name: 'keywords', content: keywords });
    }

    if (title) {
      metaTags.push(
        { property: 'og:title', content: title },
        { name: 'twitter:title', content: title }
      );
    }

    if (ogImage) {
      metaTags.push(
        { property: 'og:image', content: ogImage },
        { name: 'twitter:image', content: ogImage }
      );
    }

    // Update or create meta tags
    metaTags.forEach(({ name, property, content }) => {
      const selector = name ? `meta[name="${name}"]` : `meta[property="${property}"]`;
      let meta = document.querySelector(selector) as HTMLMetaElement;
      
      if (!meta) {
        meta = document.createElement('meta');
        if (name) meta.name = name;
        if (property) meta.setAttribute('property', property);
        document.head.appendChild(meta);
      }
      meta.content = content;
    });

    // Update canonical
    let canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.rel = 'canonical';
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.href = canonical || `${siteUrl}${location.pathname}`;

    // Update last-modified
    let lastModified = document.querySelector('meta[name="last-modified"]') as HTMLMetaElement;
    if (!lastModified) {
      lastModified = document.createElement('meta');
      lastModified.name = 'last-modified';
      document.head.appendChild(lastModified);
    }
    lastModified.content = new Date().toISOString().split('T')[0];

    // Inject JSON-LD
    const jsonLdScripts: HTMLScriptElement[] = [];
    if (jsonLd) {
      const jsonLdArray = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      jsonLdArray.forEach((data) => {
        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.text = JSON.stringify(data);
        script.setAttribute('data-seo-jsonld', 'true');
        document.head.appendChild(script);
        jsonLdScripts.push(script);
      });
    }

    // Cleanup function
    return () => {
      jsonLdScripts.forEach((script) => {
        if (script.parentNode) {
          script.parentNode.removeChild(script);
        }
      });
    };
  }, [title, description, keywords, canonical, jsonLd, ogImage, location.pathname, siteUrl]);

  return null;
}